import React from 'react'
import { useMutation } from 'react-relay'
import { toast } from 'react-toastify'

import { GraphQLError } from '@src/RelayEnvironment'
import { ApploginTelegramMutation } from '@src/__generated__/ApploginTelegramMutation.graphql'

// TODO: Why is this mutation in @src/App?
//       This **has to** create a circular dependency at some point
import { loginTelegramMutation } from '@src/App'
import formatError from '@src/components/errorFormatter'

import { useTelegram } from '@src/hooks/telegram'
import { createDebugger } from '@src/utils/debug'

import type { User, UserInformation } from '@src/stores/auth/user'
import {  useAuthUser, useAuthToken } from '@src/stores/auth/user'
import { fingerprint } from '../init/useLogin'

// useTelegramLogin handles logging in through Telegram
export const useTelegramLogin = () => {
  const [signUserInTelegram] = useMutation<ApploginTelegramMutation>(loginTelegramMutation)
  const { user: telegram, init, start } = useTelegram()
  const token = useAuthToken()
  const user = useAuthUser()

  const debug = createDebugger('telegram')
  return React.useCallback(() => {
    if (token && user) {
      if (telegram) {
        // check if already logged in
        if (usernamesMatch(user.username, telegram.username)) {
          debug.alert('Usernames match')
          return Promise.resolve({ user, token })
        }
      }
    }

    if (!init.raw) {
      // check if contains telegram referral
      debug.alert('No telegram referral')
      return null
    }

    debug.alert('Signing in')
    return new Promise<UserInformation>((resolve, reject) => {
      signUserInTelegram({
        variables: {
          input: {
            initData: init.raw,
            referralStr: start.raw,
            meta: fingerprint,
          },
        },
        onCompleted({ loginTelegram }) {
          if (!loginTelegram) {
            toast.error('Unknown error, please try again')
            debug.error('data.loginTelegram is invalid:', loginTelegram)
            debug.alert('Response missing')
            return reject(new Error('Response missing'))
          }

          if (!loginTelegram.user) {
            toast.error('Unknown error, please try again')
            debug.error('data.loginTelegram.user is missing')
            debug.alert('Response missing user')
            return reject(new Error('Response missing user'))
          }

          const user = loginTelegram.user as User
          const token = loginTelegram.token

          localStorage.setItem('pick_wallet_with_balance', '1')

          debug.alert(`Logged in as ${user.username ?? user.uuid}`)
          return resolve({ user, token })
        },
        onError(err) {
          toast.error('Telegram login failed')

          switch (true) {
            case err instanceof GraphQLError:
              const message = formatError(err)[0]
              debug.error(`Telegram login failed: ${message}`)
              debug.alert(`Login failed: ${message}`)
              return reject(err)
            default:
              debug.error(`Telegram login failed: ${err.message}`)
              debug.alert(`Login failed: ${err.message}`)
              return reject(err)
          }
        },
      })
    })
  }, [user, telegram, init, start])
}

function usernamesMatch(current: null | undefined | string, previous: null | undefined | string) {
  if (!current || !previous) return false

  const normPrevious = previous.startsWith('@') ? previous.slice(1) : previous
  const normCurrent = current.startsWith('@') ? current.slice(1) : current

  return normPrevious === normCurrent
}
